import { Link } from "react-router-dom";
import { ArrowUpRight, Clock, MapPin, Mountain, Calendar } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../ui/accordion";
import { img, money, fmtDate, expUrl, catBy, label } from "../../lib/api";

export function ExperienceCard({ e, i = 0 }) {
  const nd = e.next_departure;
  return (
    <article className="card group flex flex-col fade-up" style={{ animationDelay: `${i * 60}ms` }} data-testid={`experience-card-${e.slug}`}>
      <Link to={expUrl(e)} className="img-zoom relative block aspect-[4/3] overflow-hidden bg-[#F5F5F5]">
        <img src={img(e.cover_image)} alt={e.cover_alt || e.name} loading="lazy" className="h-full w-full object-cover" />
        <div className="absolute top-3 left-3 flex gap-2">
          <span className="chip-solid">{catBy(e.category).label}</span>
          {e.is_sample && <span className="chip bg-white">Sample listing</span>}
        </div>
      </Link>
      <div className="p-5 flex flex-col gap-3 flex-1">
        {e.destination && <p className="flex items-center gap-1.5 text-xs text-[#666]"><MapPin className="h-3.5 w-3.5" />{e.destination.name}</p>}
        <h3 className="font-display text-xl font-bold leading-snug"><Link to={expUrl(e)} className="hover:underline underline-offset-4">{e.name}</Link></h3>
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-[#333]">
          {e.duration && <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{e.duration}</span>}
          {e.difficulty && <span className="flex items-center gap-1"><Mountain className="h-3.5 w-3.5" />{label(e.difficulty)}</span>}
          {nd && <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" />{nd.status === "sold_out" ? "Next: sold out" : `Next: ${fmtDate(nd.start_date)}`}</span>}
        </div>
        <div className="mt-auto pt-4 border-t border-[#E5E5E5] flex items-end justify-between">
          <div>
            {e.base_price_minor > 0 && <><p className="eyebrow">From</p><p className="text-lg font-bold" data-testid="card-price">{money(e.base_price_minor, e.currency)}</p></>}
          </div>
          <Link to={expUrl(e)} className="btn-primary btn-sm py-2" data-testid={`view-details-${e.slug}`}>View Details <ArrowUpRight className="h-3.5 w-3.5" /></Link>
        </div>
      </div>
    </article>
  );
}

export const Empty = ({ title = "Nothing here yet", text, children }) => (
  <div className="border border-dashed border-[#E5E5E5] bg-white p-10 text-center" data-testid="empty-state">
    <p className="font-display text-xl font-bold">{title}</p>
    {text && <p className="mt-2 text-sm text-[#666]">{text}</p>}
    {children && <div className="mt-5">{children}</div>}
  </div>
);

export const SectionHead = ({ eyebrow, title, children, light }) => (
  <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
    <div className="max-w-2xl">
      {eyebrow && <p className={`eyebrow mb-3 ${light ? "!text-white/60" : ""}`}>{eyebrow}</p>}
      <h2 className="section-title">{title}</h2>
    </div>
    {children}
  </div>
);

export const Breadcrumbs = ({ items }) => (
  <nav aria-label="Breadcrumb" className="text-xs text-[#666]" data-testid="breadcrumbs">
    <ol className="flex flex-wrap items-center gap-2">
      {items.map((it, i) => (
        <li key={i} className="flex items-center gap-2">
          {i > 0 && <span>/</span>}
          {i < items.length - 1 ? <Link to={it.to} className="hover:text-black hover:underline">{it.name}</Link> : <span className="text-black" aria-current="page">{it.name}</span>}
        </li>
      ))}
    </ol>
  </nav>
);

export const PageHero = ({ eyebrow, title, text, image, alt, children }) => (
  <section className="relative bg-black text-white on-dark overflow-hidden">
    {image && <img src={img(image)} alt={alt || ""} className="absolute inset-0 h-full w-full object-cover opacity-60" />}
    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-black/20" />
    <div className="relative container-x py-24 lg:py-32">
      {eyebrow && <p className="eyebrow !text-white/70 mb-4 fade-up">{eyebrow}</p>}
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight max-w-3xl fade-up" data-testid="page-title">{title}</h1>
      {text && <p className="mt-6 max-w-2xl text-base md:text-lg text-white/85 fade-up" style={{ animationDelay: "120ms" }}>{text}</p>}
      {children}
    </div>
  </section>
);

export const FaqList = ({ faqs, testid = "faq-list" }) =>
  !faqs?.length ? null : (
    <Accordion type="single" collapsible className="border-t border-black" data-testid={testid}>
      {faqs.map((f, i) => (
        <AccordionItem key={f.id || i} value={`f${i}`} className="border-b border-[#E5E5E5]">
          <AccordionTrigger className="py-5 text-left font-display text-lg font-semibold hover:no-underline" data-testid={`faq-trigger-${i}`}>{f.q}</AccordionTrigger>
          <AccordionContent className="pb-6 text-[#333] leading-relaxed whitespace-pre-line">{f.a}</AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );

export const Spinner = () => <div className="py-24 flex justify-center" data-testid="loading"><div className="h-8 w-8 border-2 border-black border-t-transparent rounded-full animate-spin" /></div>;
